import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConversations,
  type AdminConversationSessionSummary,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { formatMicroUsd } from "@admin/lib/conversation-display";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { Button } from "@admin/components/ui/button";

export default function ConversationsPage() {
  const [items, setItems] = useState<AdminConversationSessionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await getConversations({ limit, offset });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load conversations");
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Conversations</h1>
      <p className="text-sm text-muted-fg">
        Opt-in session transcripts synced from user workspaces. Click a row for the full
        transcript, tool calls, and attachments.
      </p>
      {err && <p className="text-danger">{err}</p>}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/30 text-xs text-muted-fg">
            <tr>
              <th className="px-3 py-2">Started</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2">Msgs</th>
              <th className="px-3 py-2">Cost</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-fg">
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-fg">
                  No conversations yet.
                </td>
              </tr>
            ) : (
              items.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/20">
                  <td className="px-3 py-2 text-xs">{formatDate(s.startedAt)}</td>
                  <td className="px-3 py-2 text-xs">
                    <Link to={`/users/${s.userId}`} className="text-accent hover:underline">
                      {s.userId.slice(0, 8)}...
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs">{s.source}</td>
                  <td className="px-3 py-2">
                    <Link to={`/conversations/${s.id}`} className="text-accent hover:underline">
                      {s.title ?? s.agentSessionId.slice(0, 12)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{s.model ?? "-"}</td>
                  <td className="px-3 py-2 tabular-nums">{s.messageCount}</td>
                  <td className="px-3 py-2 tabular-nums text-xs">
                    {formatMicroUsd(s.actualCostUsdMicro)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={offset === 0 || loading}
          onClick={() => setOffset((o) => Math.max(0, o - limit))}
        >
          Previous
        </Button>
        <span className="text-xs text-muted-fg">
          {offset + 1}-{Math.min(offset + limit, total)} of {total}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={offset + limit >= total || loading}
          onClick={() => setOffset((o) => o + limit)}
        >
          Next
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Sync note</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-fg">
          Data appears when users opt in at workspace creation and their workspace sidecar syncs
          every ~90 seconds. Users who opt out stop sending new data immediately.
        </CardContent>
      </Card>
    </div>
  );
}
